/**
 * React wrapper for Bryntum ProjectModel
 */
import React from 'react';
import { AssignmentModel, AssignmentModelConfig, AssignmentStore, AssignmentStoreConfig, CalendarManagerStore, CalendarManagerStoreConfig, CalendarModel, CalendarModelConfig, CoreStateTrackingManager, CrudManagerStoreDescriptor, DependencyModel, DependencyModelConfig, DependencyStore, DependencyStoreConfig, Model, ModelConfig, RelationConfig, ResourceModel, ResourceModelConfig, ResourceStore, ResourceStoreConfig, StateTrackingManagerConfig, Store, StoreConfig, TaskModel, TaskModelConfig, TaskStore, TaskStoreConfig, TimeSpan, TimeSpanConfig } from '@bryntum/gantt';
import { ProjectModel } from '@bryntum/gantt';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumProjectModelProps =  {
     // Configs only
    adjustDurationToDST?          : boolean
    assignmentModelClass?         : typeof AssignmentModel
    assignmentsData?              : Partial<AssignmentModelConfig>[]|AssignmentModel[]
    assignmentStoreClass?         : typeof AssignmentStore
    autoLoad?                     : boolean
    autoSync?                     : boolean
    autoSyncTimeout?              : number
    bubbleEvents?                 : object
    calendarManagerStoreClass?    : typeof CalendarManagerStore
    calendarModelClass?           : typeof CalendarModel
    calendarsData?                : Partial<CalendarModelConfig>[]|CalendarModel[]
    callOnFunctions?              : boolean
    delayCalculation?             : boolean
    dependenciesData?             : Partial<DependencyModelConfig>[]|DependencyModel[]
    dependencyModelClass?         : typeof DependencyModel
    dependencyStoreClass?         : typeof DependencyStore
    encoder?                      : object
    eventsData?                   : Partial<TaskModelConfig>[]|TaskModel[]
    expanded?                     : boolean
    listeners?                    : object
    maxCalendarRange?             : number
    orderedParentIndex?           : number
    parentId?                     : string|number|null
    parentIndex?                  : number
    phantomIdField?               : string
    phantomParentIdField?         : string
    resetIdsBeforeSync?           : boolean
    resetUndoRedoQueuesAfterLoad? : boolean
    resourceModelClass?           : typeof ResourceModel
    resourcesData?                : Partial<ResourceModelConfig>[]|ResourceModel[]
    resourceStoreClass?           : typeof ResourceStore
    silenceInitialCommit?         : boolean
    skipSuccessProperty?          : boolean
    storeIdProperty?              : string
    supportShortSyncResponse?     : boolean
    taskModelClass?               : typeof TaskModel
    tasksData?                    : Partial<TaskModelConfig>[]|TaskModel[]
    taskStoreClass?               : typeof TaskStore
    timeRangesData?               : Partial<TimeSpanConfig>[]|TimeSpan[]
    trackResponseType?            : boolean
    transport?                    : object
    validateResponse?             : boolean
    writeAllFields?               : boolean

     // Configs and properties
    addConstraintOnDateSet?                   : boolean
    assignments?                              : AssignmentModel[]|Partial<AssignmentModelConfig>[]
    assignmentStore?                          : AssignmentStore|Partial<AssignmentStoreConfig>
    autoCalculatePercentDoneForParentTasks?   : boolean
    calendar?                                 : CalendarModel|string|Partial<CalendarModelConfig>
    calendarManagerStore?                     : CalendarManagerStore|Partial<CalendarManagerStoreConfig>
    calendars?                                : CalendarModel[]|Partial<CalendarModelConfig>[]
    children?                                 : boolean|object[]|Model[]|Partial<ModelConfig>[]
    crudStores?                               : CrudManagerStoreDescriptor[]|Store[]|string[]
    daysPerMonth?                             : number
    daysPerWeek?                              : number
    dependencies?                             : DependencyModel[]|Partial<DependencyModelConfig>[]
    dependenciesCalendar?                     : string
    dependencyStore?                          : DependencyStore|Partial<DependencyStoreConfig>
    direction?                                : 'Forward'|'Backward'
    enableProgressNotifications?              : boolean
    endDate?                                  : string|Date
    eventStore?                               : TaskStore|object|Partial<TaskStoreConfig>
    forceSync?                                : boolean
    hoursPerDay?                              : number
    id?                                       : string|number
    ignoreRemoteChangesInSTM?                 : boolean
    loadUrl?                                  : string
    readOnly?                                 : boolean
    resources?                                : ResourceModel[]|Partial<ResourceModelConfig>[]
    resourceStore?                            : ResourceStore|Partial<ResourceStoreConfig>
    skipNonWorkingTimeWhenSchedulingManually? : boolean
    startDate?                                : string|Date
    stm?                                      : CoreStateTrackingManager|Partial<StateTrackingManagerConfig>
    syncApplySequence?                        : CrudManagerStoreDescriptor[]|string[]
    syncUrl?                                  : string
    tasks?                                    : TaskModel[]|Partial<TaskModelConfig>[]
    taskStore?                                : TaskStore|Partial<TaskStoreConfig>
    timeRanges?                               : TimeSpan[]|Partial<TimeSpanConfig>[]
    timeRangeStore?                           : Store|Partial<StoreConfig>
    timeZone?                                 : string|number
    trackProjectModelChanges?                 : boolean

     // Properties only
    allChildren?                : Model[]
    autoExposeFields?           : boolean
    childrenField?              : string
    convertEmptyParentToLeaf?   : boolean|object
    criticalPaths?              : any[]
    defaults?                   : object
    descendantCount?            : number
    hasGeneratedId?             : boolean
    idField?                    : string
    inlineData?                 : object
    internalId?                 : number
    isCommitting?               : boolean
    isCreating?                 : boolean
    isValid?                    : boolean
    json?                       : string
    previousSiblingsTotalCount? : number
    relations?                  : Record<string, RelationConfig>
    visibleDescendantCount?     : number

     // Events
    onBeforeDestroy?       : Function
    onBeforeLoad?          : Function
    onBeforeLoadApply?     : Function
    onBeforeResponseApply? : Function
    onBeforeSend?          : Function
    onBeforeSync?          : Function
    onBeforeSyncApply?     : Function
    onCatchAll?            : Function
    onChange?              : Function
    onCycle?               : Function
    onDataReady?           : Function
    onDestroy?             : Function
    onEmptyCalendar?       : Function
    onHasChanges?          : Function
    onLoad?                : Function
    onLoadCanceled?        : Function
    onLoadFail?            : Function
    onNoChanges?           : Function
    onProgress?            : Function
    onRequestDone?         : Function
    onRequestFail?         : Function
    onSchedulingConflict?  : Function
    onSync?                : Function
    onSyncCanceled?        : Function
    onSyncDelayed?         : Function
    onSyncFail?            : Function

}

export class BryntumProjectModel extends React.Component<BryntumProjectModelProps> {

    static instanceClass = ProjectModel;

    static instanceName = 'ProjectModel';

    processWidgetContent = processWidgetContent;

    dataStores = {
        'assignmentStore': 'assignments',
        'calendarManagerStore': 'calendars',
        'dependencyStore': 'dependencies',
        'eventStore': 'events',
        'resourceStore': 'resources',
        'taskStore': 'tasks',
        'timeRangeStore': 'timeRanges'
    };

    static configNames = [
        'adjustDurationToDST',
        'assignmentModelClass',
        'assignmentsData',
        'assignmentStoreClass',
        'autoLoad',
        'autoSync',
        'autoSyncTimeout',
        'bubbleEvents',
        'calendarManagerStoreClass',
        'calendarModelClass',
        'calendarsData',
        'callOnFunctions',
        'delayCalculation',
        'dependenciesData',
        'dependencyModelClass',
        'dependencyStoreClass',
        'encoder',
        'eventsData',
        'expanded',
        'listeners',
        'maxCalendarRange',
        'orderedParentIndex',
        'parentId',
        'parentIndex',
        'phantomIdField',
        'phantomParentIdField',
        'resetIdsBeforeSync',
        'resetUndoRedoQueuesAfterLoad',
        'resourceModelClass',
        'resourcesData',
        'resourceStoreClass',
        'silenceInitialCommit',
        'skipSuccessProperty',
        'storeIdProperty',
        'supportShortSyncResponse',
        'taskModelClass',
        'tasksData',
        'taskStoreClass',
        'timeRangesData',
        'trackResponseType',
        'transport',
        'validateResponse',
        'writeAllFields'
    ];

    static propertyConfigNames = [
        'addConstraintOnDateSet',
        'assignments',
        'assignmentStore',
        'autoCalculatePercentDoneForParentTasks',
        'calendar',
        'calendarManagerStore',
        'calendars',
        'children',
        'crudStores',
        'daysPerMonth',
        'daysPerWeek',
        'dependencies',
        'dependenciesCalendar',
        'dependencyStore',
        'direction',
        'enableProgressNotifications',
        'endDate',
        'eventStore',
        'forceSync',
        'hoursPerDay',
        'id',
        'ignoreRemoteChangesInSTM',
        'loadUrl',
        'onBeforeDestroy',
        'onBeforeLoad',
        'onBeforeLoadApply',
        'onBeforeResponseApply',
        'onBeforeSend',
        'onBeforeSync',
        'onBeforeSyncApply',
        'onCatchAll',
        'onChange',
        'onCycle',
        'onDataReady',
        'onDestroy',
        'onEmptyCalendar',
        'onHasChanges',
        'onLoad',
        'onLoadCanceled',
        'onLoadFail',
        'onNoChanges',
        'onProgress',
        'onRequestDone',
        'onRequestFail',
        'onSchedulingConflict',
        'onSync',
        'onSyncCanceled',
        'onSyncDelayed',
        'onSyncFail',
        'readOnly',
        'resources',
        'resourceStore',
        'skipNonWorkingTimeWhenSchedulingManually',
        'startDate',
        'stm',
        'syncApplySequence',
        'syncUrl',
        'tasks',
        'taskStore',
        'timeRanges',
        'timeRangeStore',
        'timeZone',
        'trackProjectModelChanges'
    ];

    static propertyNames = [
        'allChildren',
        'autoExposeFields',
        'childrenField',
        'convertEmptyParentToLeaf',
        'criticalPaths',
        'defaults',
        'descendantCount',
        'hasGeneratedId',
        'idField',
        'inlineData',
        'internalId',
        'isCommitting',
        'isCreating',
        'isValid',
        'json',
        'previousSiblingsTotalCount',
        'relations',
        'visibleDescendantCount'
    ];

    // Component instance
    instance!: ProjectModel;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumProjectModelProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        return null

    }
}
